/**
 * @author: Wangwei
 * @description: 打卡营学习可操作模式
 */
import { useRequest } from 'umi';
import styles from '@/pages/camp/css/studyIndexCSS.less';
import React, { useState, useEffect, useRef, memo, useMemo } from 'react';
import cx from 'classnames';
import { Form, Button, Rate, message, Anchor, Skeleton } from 'antd';
import { getNodeContent, postNode, postNodeSave } from '@/utils/requests/camp/requests_study';
import { NodeContentType } from '@/types/camp/study/index';
import {
  NodeContentTypes,
  ContentTypes,
  TextType,
  VideoType,
  CertificationType,
  VideoLogParamsType,
} from '@/types/camp/study/contentTypes';
import Videos from './video';
import Cert from './cert';
import QuestionNodeFail from './questionNodeFail';
import Editor from '@digit-fe/digit-editor-new';
import { editParseJson } from '@/utils/index';

interface Props {
  nodeId: string;
}

const StudyEditPreview: React.FC<Props> = ({ nodeId }) => {
  const [nodeContentData, setNodeContentData] = useState<NodeContentTypes>();
  const [videoLogParams, setVideoLogParams] = useState<VideoLogParamsType>();
  const [contentData, setContentData] = useState<ContentTypes[]>([]);
  // 习题表单数据操作
  const [exercises, setExercises] = useState<ContentTypes[]>([]);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);

  // 获取节点模块内容
  const { run: getNodeContentRequest } = useRequest(
    (nodeId) => {
      return getNodeContent(nodeId);
    },
    {
      manual: true,
      onSuccess: (res) => {
        setNodeContentData(res);
        // 视频埋点所需参数
        const parames: VideoLogParamsType = {
          batchId: res?.studyNode.batchId,
          nodeId: res?.id,
        };

        setVideoLogParams(parames);
      },
    },
  );

  useEffect(() => {
    if (nodeId) {
      setLoading(true);
      getNodeContentRequest(nodeId);
    }
  }, [nodeId]);

  useEffect(() => {
    // 提取模块内容
    setContentData(nodeContentData?.contents || []);
    setTimeout(function () {
      nodeContentData?.id && setLoading(false);
    }, 600);
  }, [nodeContentData]);

  // 悬浮栏目导航
  const anchorItems = contentData.map((item, index) => ({
    key: item.id,
    href: `#${item.id}`,
    title: item.name,
  }));
  anchorItems.push(
    {
      key: 'rate_camp',
      href: '#rate_camp',
      title: '打卡心得',
    },
    {
      key: 'rate_study',
      href: '#rate_study',
      title: '学习评价',
    },
  );

  // 处理不同板块内容
  const handleContentItem = (item: ContentTypes, index: number): React.ReactNode | null => {
    let result: React.ReactNode | null = null;
    switch (item.type) {
      case NodeContentType.RICH_TEXT:
        if (item.info && typeof item.info === 'object') {
          result = (
            <Editor
              content={editParseJson((item.info as TextType).richText)}
              editable={false}
              titleProps={{ showTitle: false }}
              catalogProps={{ showCatalog: false }}
            />
          );
        }
        break;
      case NodeContentType.EXERCISES:
        if (item.info && typeof item.info === 'object') {
          result = (
            <QuestionNodeFail
              item={item}
              handleAddItemOrUpdate={handleAddItemOrUpdate}
              form={form}
            />
          );
        }
        break;
      case NodeContentType.VIDEO:
        if (item.info && typeof item.info === 'object') {
          result = (
            <Videos
              videoList={item.info as VideoType}
              videoLogParams={videoLogParams}
              contentId={item.id}
            />
          );
        }
        break;
      case NodeContentType.PROFESSIONAL_CERTIFICATION:
        if (item.info) {
          result = <Cert data={item.info as CertificationType} form={form} />;
        }
        break;
      default:
        result = null;
    }

    result = (
      <div style={{ marginBottom: 40 }}>
        <div id={item.id} className="f24 fw600" style={{ marginBottom: 16 }}>
          {item.name}
        </div>
        <Form form={form} key={index} layout="vertical">
          {result}
        </Form>
      </div>
    );

    return result;
  };

  // 保存习题
  const handleAddItemOrUpdate = (newItem: ContentTypes) => {
    const newExercises = exercises.find((item) => item.id === newItem.id);
    if (newExercises) {
      const updatedExercises = exercises.map((item) =>
        item.id === newItem.id ? { ...item, ...newItem } : item,
      );
      setExercises(updatedExercises);
    } else {
      setExercises([...exercises, newItem]);
    }
  };

  const renderedItems = useMemo(() => {
    return contentData.map((item, index) => (
      <div key={index}>{handleContentItem(item, index)}</div>
    ));
  }, [contentData]);

  return (
    <>
      <div>
        {loading && (
          <div style={{ padding: 24 }}>
            <div style={{ width: '90%', marginBottom: 24, padding: '10px 0' }}>
              <Skeleton.Button active block style={{ height: 40 }} />
            </div>

            <div className="flex_0" style={{ width: '90%' }}>
              <div style={{ width: 120 }}>
                <Skeleton.Image active />
              </div>
              <div style={{ flex: 1 }}>
                <Skeleton active loading={loading} paragraph={{ rows: 6 }} />
              </div>
            </div>
          </div>
        )}

        {!loading && (
          <div>
            <div className={cx(styles.top_node_title, 'flex_3')}>
              <div className={styles.node_title}>{nodeContentData?.name}</div>
              <div className={cx(styles.top_node_state, styles.top_node_state_fail)}>
                本节点打卡失败
              </div>
            </div>
            <div className={styles.node_content_main}>
              <div className={styles.node_content}>
                {contentData && renderedItems}
                <div className={styles.new_antd_anchor}>
                  <Anchor items={anchorItems} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export default memo(StudyEditPreview);
